<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const store = useStore();

// ── Local state ──────────────────────────────────────────────────────────────
const inboxId = ref(null);
const recipientType = ref('contact');
const recipientContactId = ref(null);
const recipientSearch = ref('');
const contactResults = ref([]);
const contactSearchLoading = ref(false);
const messageType = ref('custom');
const selectedTemplate = ref(null);
const templateVariables = ref({});
const content = ref('');

// ── Inboxes ───────────────────────────────────────────────────────────────────
const messagingInboxes = computed(() =>
  store.getters['inboxes/getInboxes'].filter(i =>
    ['Channel::Whatsapp', 'Channel::Instagram'].includes(i.channel_type)
  )
);

const selectedInbox = computed(() =>
  messagingInboxes.value.find(i => i.id === inboxId.value) || null
);

const isWhatsAppCloud = computed(
  () =>
    selectedInbox.value?.channel_type === 'Channel::Whatsapp' &&
    selectedInbox.value?.provider === 'whatsapp_cloud'
);

const isInstagram = computed(
  () => selectedInbox.value?.channel_type === 'Channel::Instagram'
);

const show24hWarning = computed(() => isWhatsAppCloud.value || isInstagram.value);

// ── Contact search ────────────────────────────────────────────────────────────
let searchTimeout = null;
const onSearchInput = () => {
  clearTimeout(searchTimeout);
  if (recipientSearch.value.length < 2) {
    contactResults.value = [];
    return;
  }
  searchTimeout = setTimeout(async () => {
    contactSearchLoading.value = true;
    try {
      const res = await ContactAPI.search(recipientSearch.value, 1);
      contactResults.value = res.data?.payload || [];
    } finally {
      contactSearchLoading.value = false;
    }
  }, 300);
};

const selectContact = contact => {
  recipientContactId.value = contact.id;
  recipientSearch.value = `${contact.name}${contact.phone_number ? ` (${contact.phone_number})` : ''}`;
  contactResults.value = [];
  emitValue();
};

const clearContact = () => {
  recipientContactId.value = null;
  recipientSearch.value = '';
  contactResults.value = [];
  emitValue();
};

// ── Template selection ────────────────────────────────────────────────────────
const onTemplateSelect = template => {
  selectedTemplate.value = template;
  templateVariables.value = {};
  emitValue();
};

const templateVariableKeys = computed(() => {
  if (!selectedTemplate.value?.variable_schema) return [];
  return selectedTemplate.value.variable_schema;
});

const onVariableInput = () => emitValue();

// ── Emit ──────────────────────────────────────────────────────────────────────
const emitValue = () => {
  emit('update:modelValue', [
    {
      inbox_id: inboxId.value,
      recipient_type: recipientType.value,
      recipient_contact_id:
        recipientType.value === 'other' ? recipientContactId.value : null,
      message_type: isWhatsAppCloud.value ? messageType.value : 'custom',
      template_id: messageType.value === 'template' ? selectedTemplate.value?.id : null,
      template_variables: messageType.value === 'template' ? { ...templateVariables.value } : {},
      content: messageType.value === 'custom' ? content.value : '',
    },
  ]);
};

// Reset message-type-specific state when inbox changes
watch(inboxId, () => {
  messageType.value = 'custom';
  selectedTemplate.value = null;
  templateVariables.value = {};
  content.value = '';
  emitValue();
});

watch(recipientType, () => {
  recipientContactId.value = null;
  recipientSearch.value = '';
  contactResults.value = [];
  emitValue();
});

// ── Initialize from saved value ───────────────────────────────────────────────
onMounted(() => {
  const saved = Array.isArray(props.modelValue) ? props.modelValue[0] : null;
  if (!saved) return;
  inboxId.value = saved.inbox_id ?? null;
  recipientType.value = saved.recipient_type ?? 'contact';
  recipientContactId.value = saved.recipient_contact_id ?? null;
  messageType.value = saved.message_type ?? 'custom';
  content.value = saved.content ?? '';
  templateVariables.value = saved.template_variables ?? {};
  if (saved.template_id) {
    // Templates are in Vuex; try to restore selection for display
    const templates = store.getters['inboxes/getFilteredWhatsAppTemplates'](saved.inbox_id);
    selectedTemplate.value = templates.find(t => t.id === saved.template_id) || null;
  }
});
</script>

<template>
  <div class="flex flex-col gap-2 mt-1 p-2 rounded-lg bg-n-alpha-1 border border-n-alpha-2">
    <!-- Inbox selector -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.INBOX_LABEL') }}
      </label>
      <select
        v-model="inboxId"
        class="w-full rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
        @change="emitValue"
      >
        <option value="" disabled selected>
          {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.INBOX_PLACEHOLDER') }}
        </option>
        <option v-for="inbox in messagingInboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
          <template v-if="inbox.channel_type === 'Channel::Instagram'">
            (Instagram)
          </template>
          <template v-else-if="inbox.provider === 'whatsapp_cloud'">
            (WhatsApp Oficial)
          </template>
          <template v-else>
            (WhatsApp)
          </template>
        </option>
      </select>
      <p v-if="messagingInboxes.length === 0" class="text-xs text-n-slate-9">
        {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.NO_INBOXES') }}
      </p>
    </div>

    <template v-if="selectedInbox">
      <!-- Recipient -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.RECIPIENT_LABEL') }}
        </label>
        <div class="flex gap-3">
          <label class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer">
            <input
              v-model="recipientType"
              type="radio"
              value="contact"
              class="accent-n-brand"
              @change="emitValue"
            />
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.RECIPIENT_CARD_CONTACT') }}
          </label>
          <label class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer">
            <input
              v-model="recipientType"
              type="radio"
              value="other"
              class="accent-n-brand"
              @change="emitValue"
            />
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.RECIPIENT_OTHER') }}
          </label>
        </div>

        <!-- Contact search for "other" recipient -->
        <div v-if="recipientType === 'other'" class="relative mt-1">
          <div class="flex gap-1">
            <input
              v-model="recipientSearch"
              type="text"
              class="flex-1 rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
              :placeholder="t('AUTOMATION.ACTION.INBOX_MESSAGE.RECIPIENT_SEARCH_PLACEHOLDER')"
              @input="onSearchInput"
            />
            <button
              v-if="recipientContactId"
              class="flex items-center justify-center w-7 h-7 rounded-lg border border-n-weak text-n-slate-9 hover:text-n-ruby-9"
              type="button"
              @click="clearContact"
            >
              <span class="i-lucide-x h-3.5 w-3.5" />
            </button>
          </div>
          <!-- Results dropdown -->
          <div
            v-if="contactResults.length > 0"
            class="absolute left-0 right-0 z-30 mt-1 max-h-40 overflow-y-auto rounded-lg border border-n-alpha-3 bg-n-solid-1 shadow-lg"
          >
            <button
              v-for="c in contactResults"
              :key="c.id"
              type="button"
              class="flex w-full flex-col px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="selectContact(c)"
            >
              <span class="text-sm text-n-slate-12">{{ c.name }}</span>
              <span v-if="c.phone_number" class="text-xs text-n-slate-9">{{ c.phone_number }}</span>
            </button>
          </div>
          <p v-if="contactSearchLoading" class="mt-1 text-xs text-n-slate-9">
            Buscando...
          </p>
        </div>
      </div>

      <!-- Message type toggle (only for WhatsApp Cloud) -->
      <div v-if="isWhatsAppCloud" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.MESSAGE_TYPE_LABEL') }}
        </label>
        <div class="flex gap-3">
          <label class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer">
            <input
              v-model="messageType"
              type="radio"
              value="custom"
              class="accent-n-brand"
              @change="emitValue"
            />
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.MESSAGE_TYPE_CUSTOM') }}
          </label>
          <label class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer">
            <input
              v-model="messageType"
              type="radio"
              value="template"
              class="accent-n-brand"
              @change="emitValue"
            />
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.MESSAGE_TYPE_TEMPLATE') }}
          </label>
        </div>
      </div>

      <!-- Template picker -->
      <template v-if="messageType === 'template' && isWhatsAppCloud">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.TEMPLATE_LABEL') }}
          </label>
          <TemplatesPicker :inbox-id="selectedInbox.id" @on-select="onTemplateSelect" />
        </div>

        <!-- Variable inputs -->
        <div v-if="templateVariableKeys.length > 0" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.VARIABLES_LABEL') }}
          </label>
          <div
            v-for="varDef in templateVariableKeys"
            :key="varDef.key"
            class="flex flex-col gap-0.5"
          >
            <span class="text-xs text-n-slate-9">{{ varDef.key }}</span>
            <input
              v-model="templateVariables[varDef.key]"
              type="text"
              class="rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
              :placeholder="varDef.value_type === 'text' ? 'Texto...' : 'URL da mídia...'"
              @input="onVariableInput"
            />
          </div>
        </div>
      </template>

      <!-- Custom message textarea -->
      <template v-if="messageType === 'custom'">
        <!-- 24h warning -->
        <div
          v-if="show24hWarning"
          class="flex items-start gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-2.5 py-2"
        >
          <span class="i-lucide-alert-triangle h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <p class="text-xs text-amber-700 dark:text-amber-300">
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.WINDOW_WARNING') }}
          </p>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AUTOMATION.ACTION.INBOX_MESSAGE.CONTENT_LABEL') }}
          </label>
          <textarea
            v-model="content"
            rows="3"
            class="w-full resize-none rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12 placeholder-n-slate-8 focus:border-n-brand focus:outline-none"
            :placeholder="t('AUTOMATION.ACTION.INBOX_MESSAGE.CONTENT_PLACEHOLDER')"
            @input="emitValue"
          />
        </div>
      </template>
    </template>
  </div>
</template>
